/* global gapi */
/* global auth2 */

import PropTypes from 'prop-types';
import { createContext, useEffect, useReducer, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

// third-party
import { Chance } from 'chance';
import { jwtDecode } from 'jwt-decode';
import cryptoJS from "crypto-js";

// reducer - state management
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';

// project imports
import Loader from 'ui-component/Loader';
import axios from 'utils/axios';

const chance = new Chance();

// constant
const initialState = {
    isLoggedIn: false,
    isInitialized: false,
    user: null
};

const verifyToken = (serviceToken) => {
    if (!serviceToken) {
        return false;
    }
    const decoded = jwtDecode(serviceToken);
    // console.log(decoded);
    /**
     * Property 'exp' does not exist on type '<T = unknown>(token, options?: JwtDecodeOptions | undefined) => T'.
     */
    return decoded.exp > Date.now() / 1000;
};

const setSession = (serviceToken) => {
    if (serviceToken) {
        localStorage.setItem('serviceToken', serviceToken);
        axios.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
    } else {
        localStorage.removeItem('serviceToken');
        delete axios.defaults.headers.common.Authorization;
    }
};

// ==============================|| JWT CONTEXT & PROVIDER ||============================== //
const GoogleLoginContext = createContext(null);

export const GoogleLoginProvider = ({ children }) => {
    const [state, dispatch] = useReducer(accountReducer, initialState);
    const [googleLoaded, setGoogleLoaded] = useState(false);
    const onLoginSuccess = useRef(null);
    const navigate = useNavigate();

    const loadGoogleLoginScript = () => {
        // 메타 태그 추가
        const meta = document.createElement("meta");
        meta.setAttribute("name", "google-signin-client_id");
        meta.setAttribute("content", "857716028349-upaceup1cf110pbo6dsk9fat29978u93.apps.googleusercontent.com");
        document.getElementsByTagName("head")[0].appendChild(meta);

        // Google signin 스크립트 추가
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.addEventListener('load', () => {
            console.log("Google API script loaded");

            window.google.accounts.id.initialize({
                client_id: "857716028349-upaceup1cf110pbo6dsk9fat29978u93.apps.googleusercontent.com",
                callback: onGoogleCredentialResponse,
                // auto_select: true
            });
            //window.google.accounts.id.prompt();
            setGoogleLoaded(true);
        });
        document.body.appendChild(script);
    };

    // Google API 에서 credential 정보가 수신됐을때 처리
    const onGoogleCredentialResponse = async (resp) => {
        // console.log(resp);
        // console.log(jwtDecode(resp.credential));
        // setSession(resp.credential);

        onLoginSuccess.current && onLoginSuccess.current(resp.credential);
    };

    useEffect(() => {
        loadGoogleLoginScript();
        //dispatch({ type: LOGOUT });
        // getLoginInfoWithToken();
    }, []);

    useEffect(() => {
        if (googleLoaded) {
            const init = async () => {
                try {
                    const serviceToken = window.localStorage.getItem('serviceToken');
                    if (serviceToken && verifyToken(serviceToken)) {
                        setSession(serviceToken);
                        
                        const response = await axios.get('/api/me');
                        if (response.data.result == "OK") {
                            const { user } = response.data;
                            dispatch({ type: LOGIN, payload: { isLoggedIn: true, user } });
                            //navigate("/");
                        }
                        else {
                            dispatch({ type: LOGOUT });
                        }
                    } else {
                        dispatch({ type: LOGOUT });
                    }
                } catch (err) {
                    console.error(err);
                    dispatch({
                        type: LOGOUT
                    });
                }
            };
            init();
        }
    }, [googleLoaded]);

    const login = async (token) => {
        let ret = await axios.post("/api/googleauth", { token });
        if (ret.data.result == "OK") {
            const { serviceToken, user } = ret.data;
            setSession(serviceToken);

            if (user != null) {
                dispatch({ type: LOGIN, payload: { isLoggedIn: true, user } });
                return true;
            }
            else {
                dispatch({ type: LOGOUT });
                return false;
            }
        }
        else {
            dispatch({ type: LOGOUT });
            return false;
        }
    };

    const register = async (appleboxid, security, info) => {
        // todo: this flow need to be recode as it not verified
        const securitypw = cryptoJS.SHA256(security).toString();

        const response = await axios.post('/api/signup', {
            appleboxid,
            security: securitypw,
            ...info
        });

        if (response.data.result == "OK") {
            const { user } = response.data;
            dispatch({
                type: LOGIN,
                payload: {
                    isLoggedIn: true,
                    user
                }
            });
        }
        return response.data.result == "OK";
    };

    const logout = () => {
        setSession(null);
        // googleLogout();
        dispatch({ type: LOGOUT });
    };

    const resetPassword = (email) => console.log(email);

    const updateProfile = () => { };

    const me = async () => {
        const response = await axios.get('/api/me');
        const { user } = response.data;

        if (response.data.result == "OK") {
            dispatch({
                type: LOGIN,
                payload: {
                    isLoggedIn: true,
                    user: { ...user }
                }
            });
            return true;
        }
        else return false;
    };

    const setLoginCallback = (callback) => {
        onLoginSuccess.current = callback;
    };

    if (state.isInitialized !== undefined && !state.isInitialized) {
        return <Loader />;
    }

    if (!googleLoaded) {
        return <Loader />;
    }

    return (
        <GoogleLoginContext.Provider value={{
            ...state,
            login,
            logout,
            register,
            resetPassword,
            updateProfile,
            setLoginCallback,
            me
        }}>{children}
        </GoogleLoginContext.Provider>
    );
};

GoogleLoginProvider.propTypes = {
    children: PropTypes.node
};

export default GoogleLoginContext;
